import { lazy, Suspense, useState } from "react";
import {
  Grid,
  Layout,
  Menu,
  Spin,
  Button,
  Drawer,
  Dropdown,
  Avatar,
  Radio,
  Segmented,
  Space,
  Typography,
  type MenuProps,
} from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  CameraOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  RobotOutlined,
  SettingOutlined,
  BgColorsOutlined,
  CheckOutlined,
  TeamOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WarningOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { auth } from "./api";
import Login from "./pages/Login";
import { useAppearance, type Design } from "./theme";

// Экраны тяжёлые из-за таблиц, графика и XLSX. Загружаем только открытый экран,
// чтобы вход в PLAN не тянул весь интерфейс сразу.
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Inconsistencies = lazy(() => import("./pages/Inconsistencies"));
const Snapshots = lazy(() => import("./pages/Snapshots"));
const Resources = lazy(() => import("./pages/Resources"));
const Gantt = lazy(() => import("./pages/Gantt"));
const Meetings = lazy(() => import("./pages/Meetings"));
const Ai = lazy(() => import("./pages/Ai"));
const Plans = lazy(() => import("./pages/Plans"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const Materials = lazy(() => import("./pages/Materials"));
const Audit = lazy(() => import("./pages/Audit"));

const ROLE_LABEL: Record<string, string> = {
  viewer: "чтение",
  editor: "правка",
  executive: "руководство",
  admin: "администратор",
};

const PAGE_LABELS: Record<string, string> = {
  "/dashboard": "Главная",
  "/portfolio": "Портфель",
  "/inconsistencies": "Нестыковки",
  "/snapshots": "Снимки",
  "/plans": "Поручения",
  "/gantt": "План-графики",
  "/resources": "Загрузка людей",
  "/meetings": "Совещания",
  "/materials": "Материалы",
  "/ai": "Поиск и AI",
  "/admin": "Администрирование",
  "/audit": "Аудит",
};

export default function App() {
  const [authed, setAuthed] = useState(Boolean(auth.token()));
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const { design, mode, setDesign, setMode, refreshProfile } = useAppearance();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();

  if (!authed) {
    return <Login onSuccess={() => { refreshProfile(); setAuthed(true); }} />;
  }

  const items: MenuProps["items"] = [
    { type: "group", label: "Обзор", children: [{ key: "/dashboard", icon: <AppstoreOutlined />, label: "Главная" }] },
    {
      type: "group",
      label: "Управление",
      children: [
        { key: "/portfolio", icon: <ApartmentOutlined />, label: "Портфель" },
        { key: "/inconsistencies", icon: <WarningOutlined />, label: "Нестыковки" },
        { key: "/snapshots", icon: <CameraOutlined />, label: "Снимки" },
        { key: "/plans", icon: <FileTextOutlined />, label: "Поручения" },
        { key: "/gantt", icon: <CalendarOutlined />, label: "План-графики" },
        { key: "/resources", icon: <TeamOutlined />, label: "Загрузка людей" },
      ],
    },
    {
      type: "group",
      label: "Рабочее пространство",
      children: [
        { key: "/meetings", icon: <VideoCameraOutlined />, label: "Совещания" },
        { key: "/materials", icon: <FolderOpenOutlined />, label: "Материалы" },
        { key: "/ai", icon: <RobotOutlined />, label: "Поиск и AI" },
      ],
    },
    ...(auth.atLeast("executive")
      ? [{ type: "group" as const, label: "Система", children: [{ key: "/audit", icon: <AuditOutlined />, label: "Аудит" }, ...(auth.atLeast("admin") ? [{ key: "/admin", icon: <SettingOutlined />, label: "Администрирование" }] : [])] }]
      : []),
  ];

  const goTo: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
    setMobileNavOpen(false);
  };

  const logout = () => {
    auth.clear();
    refreshProfile();
    setAuthed(false);
  };

  const designs: { key: Design; title: string; description: string }[] = [
    { key: "lightence", title: "Lightence", description: "Светлая аналитическая панель с индиго-акцентами" },
    { key: "executive", title: "Executive", description: "Текущий строгий интерфейс для руководителей" },
    { key: "slate", title: "Slate", description: "Контрастная рабочая среда в холодных тонах" },
  ];

  const userMenu: MenuProps = {
    items: [
      { key: "role", icon: <UserOutlined />, label: ROLE_LABEL[auth.role()] ?? auth.role(), disabled: true },
      { type: "divider" },
      { key: "logout", label: "Выйти" },
    ],
    onClick: ({ key }) => key === "logout" && logout(),
  };

  const navigation = (inlineCollapsed = false) => (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      items={items}
      inlineCollapsed={inlineCollapsed}
      onClick={goTo}
      className="app-nav"
      aria-label="Основная навигация"
    />
  );

  return (
    <Layout className={`app-shell ${collapsed ? "app-shell--collapsed" : ""}`}>
      <Layout.Sider
        width={252}
        collapsedWidth={76}
        collapsed={collapsed}
        trigger={null}
        className="app-sidebar"
      >
        <div className="app-brand" aria-label="WAVIoT PLAN">
          <span className="app-brand__mark">W</span>
          {!collapsed && <span className="app-brand__name">WAVIoT <b>PLAN</b></span>}
        </div>
        <div className="app-sidebar__nav">{navigation(collapsed)}</div>
        <div className="app-sidebar__footer">{!collapsed && <span>Портфельное управление</span>}</div>
      </Layout.Sider>

      <Drawer
        placement="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        width={288}
        className="app-mobile-drawer"
        title={<div className="app-brand app-brand--drawer"><span className="app-brand__mark">W</span><span className="app-brand__name">WAVIoT <b>PLAN</b></span></div>}
        styles={{ body: { padding: "12px 10px" } }}
      >
        {navigation()}
      </Drawer>

      <Layout className="app-main">
        <Layout.Header className="app-topbar">
          <div className="app-topbar__leading">
            <Button
              type="text"
              className="app-topbar__menu"
              icon={screens.lg ? (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />) : <MenuOutlined />}
              onClick={() => (screens.lg ? setCollapsed(!collapsed) : setMobileNavOpen(true))}
              aria-label={screens.lg ? (collapsed ? "Развернуть навигацию" : "Свернуть навигацию") : "Открыть навигацию"}
            />
            <span className="app-topbar__section">{PAGE_LABELS[location.pathname] ?? "Главная"}</span>
          </div>
          <div className="app-topbar__actions">
            <Button type="text" className="app-appearance-button" icon={<BgColorsOutlined />} onClick={() => setAppearanceOpen(true)} aria-haspopup="dialog">
              <span>Оформление</span>
            </Button>
            <Dropdown menu={userMenu} trigger={["click"]} placement="bottomRight">
              <Button type="text" className="app-user" aria-label="Меню пользователя">
                <Avatar size={30} icon={<UserOutlined />} className="app-user__avatar" />
                <span className="app-user__details"><strong>{auth.login()}</strong><small>{ROLE_LABEL[auth.role()] ?? auth.role()}</small></span>
              </Button>
            </Dropdown>
          </div>
        </Layout.Header>

        <Drawer title="Оформление" open={appearanceOpen} onClose={() => setAppearanceOpen(false)} placement="right" width={screens.sm ? 400 : "100%"} className="appearance-drawer">
          <Typography.Paragraph type="secondary">Выберите визуальный стиль. Изменения применяются сразу и сохраняются только для вашей учётной записи на этом устройстве.</Typography.Paragraph>
          <Typography.Title level={5}>Дизайн</Typography.Title>
          <Radio.Group value={design} onChange={(event) => setDesign(event.target.value)} className="appearance-designs" aria-label="Выбор дизайна">
            {designs.map((item) => (
              <Radio.Button key={item.key} value={item.key} className={`appearance-preview appearance-preview--${item.key}`}>
                <span className="appearance-preview__window"><i /><b /><em /></span>
                <span className="appearance-preview__copy"><strong>{item.title}</strong><small>{item.description}</small></span>
                {design === item.key && <CheckOutlined className="appearance-preview__check" />}
              </Radio.Button>
            ))}
          </Radio.Group>
          <Typography.Title level={5}>Режим</Typography.Title>
          <Segmented block value={mode} onChange={(value) => setMode(value as "light" | "dark")} options={[{ label: "Светлый", value: "light" }, { label: "Тёмный", value: "dark" }]} aria-label="Выбор цветового режима" />
          <Space className="appearance-drawer__footer"><Button type="primary" onClick={() => setAppearanceOpen(false)}>Готово</Button></Space>
        </Drawer>

        <Layout.Content className="app-content">
          <Suspense fallback={<div className="app-loading"><Spin size="large" /></div>}>
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/inconsistencies" element={<Inconsistencies />} />
            <Route path="/snapshots" element={<Snapshots />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/gantt" element={<Gantt />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/materials" element={<Materials />} />
            <Route path="/ai" element={<Ai />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
